import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TypoGraphyKit from "../typoGraphy";
import CustomButton from "../button";
import theme from "@/style/theme";
import { SxProps, Theme } from "@mui/material/styles";

interface AlertDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  content?: any;
  agreeText?: string | React.ReactNode;
  disagreeText?: string;
  onAgree?: (arg?: any) => void;
  onDisagree?: () => void;
  isHeader?: boolean;
  header?: React.ReactNode;
  paragraph?: string;
  children?: React.ReactNode;
  heightMain?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  maxWidth?: "sm" | "xs" | "md" | "lg" | "xl";
  fullScreen?: boolean;

}
/**
 * AlertDialog
 *
 * نمایش یک دیالوگ هشدار/تأیید با قابلیت سفارشی‌سازی:
 * - عنوان و محتوا
 * - متن دکمه‌های تایید و انصراف
 * - قابلیت نمایش هدر سفارشی
 * - محتوای دلخواه با children
 * - کاملاً واکنش‌گرا برای موبایل، تبلت و دسکتاپ
 *
 * @param open - وضعیت باز بودن دیالوگ
 * @param onClose - تابع بسته شدن دیالوگ
 * @param title - عنوان دیالوگ (اختیاری)
 * @param content - متن محتوای دیالوگ (اختیاری)
 * @param agreeText - متن دکمه تایید (اختیاری)
 * @param disagreeText - متن دکمه انصراف (اختیاری، پیش‌فرض: "Disagree")
 * @param onAgree - تابع اجرای تایید (اختیاری)
 * @param onDisagree - تابع اجرای انصراف (اختیاری)
 * @param isHeader - تعیین نمایش هدر سفارشی (اختیاری، پیش‌فرض: false)
 * @param header - محتوای هدر سفارشی (اختیاری)
 * @param paragraph - متن پاراگراف اضافه (اختیاری)
 * @param children - محتوای دلخواه داخل دیالوگ (اختیاری)
 * @param heightMain - ارتفاع اصلی دیالوگ (اختیاری، پیش‌فرض: "578px")
 * @param disabled - غیرفعال کردن دکمه‌ها (اختیاری)
 * @returns JSX.Element
 */

export default function AlertDialog({ open, onClose, title, content, agreeText, disagreeText = "Disagree", onAgree, onDisagree, isHeader = false, header, paragraph, children, heightMain = "578px", disabled, sx, maxWidth = 'sm', fullScreen = false, }: AlertDialogProps) {

  const handleAgree = () => {
    onClose();
  };

  const handleDisagree = () => {
    onDisagree?.();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      sx={{
        "& .MuiPaper-root": fullScreen
          ? {
            width: "90%",
            height: "90%",
            maxWidth: "90%",
            maxHeight: "90%",
            borderRadius: "24px",
            overflow:'visible',
            background: "#1C1C1D",
          }
          : {
            width: "100%",
            maxWidth: { xs: "90%", sm: "500px", md: "589px" },
            minWidth: { xs: "auto", sm: "300px" },
            position: "relative",
            overflow: "visible",
            borderRadius: "24px",
            height: { xs: "auto", sm: heightMain },
            padding: { xs: "40px 15px 15px 15px", sm: "50px 20px 20px 30px" },
            background: "#1C1C1D",
          },
        ...sx,
      }}
    >

      {isHeader && (
        <DialogTitle id="alert-dialog-title" sx={{ background: "#2A2A2C", maxWidth: { xs: "80%", sm: "456px" }, minWidth: { xs: "80%", sm: "456px" }, position: "absolute", left: "50%", transform: "translateX(-50%)", top: { xs: "-35px", sm: "-45px" }, borderRadius: "24px", height: { xs: "70px", sm: "90px" }, display: "flex", justifyContent: "center", alignItems: "center", }}>
          {header}
        </DialogTitle>
      )}
      {title && (
        <DialogTitle id="alert-dialog-title" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
          {title}
        </DialogTitle>
      )}
      {paragraph && <TypoGraphyKit variant="body1">{paragraph}</TypoGraphyKit>}
      <DialogContent>
        {content && (
          <DialogContentText id="alert-dialog-description" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            {content}
          </DialogContentText>
        )}
        {children}
      </DialogContent>
      <DialogActions sx={{ gap: 2, flexWrap: "wrap", justifyContent: { xs: "center", sm: "flex-end" }, }}>
        {onDisagree && (
          <CustomButton disabled={disabled} onClick={handleDisagree} sx={{ bgcolor: theme.palette.action.disabled, color: "#fff", minWidth: { xs: "120px", sm: "auto" }, }}>
            {disagreeText}
          </CustomButton>
        )}
        {agreeText && (
          <CustomButton onClick={onAgree ? onAgree : handleAgree} disabled={disabled} sx={{ minWidth: { xs: "120px", sm: "auto" } }}>
            <TypoGraphyKit variant="body1">{agreeText}</TypoGraphyKit>
          </CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
